import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import { Button, Chip, Spinner } from "@heroui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  DocumentIcon,
  TrophyIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { VIDEO_UPLOAD_ENABLED } from "../../../config/featureFlags";
import { useUpcomingOpportunityCards } from "../../../hooks/useUpcomingOpportunityCards";
import {
  createDealProposal,
  getDealAgreement,
  getOpportunityCard,
  previewDealAgreement,
  previewDealAgreementForUpdate,
  updateDealDetails,
  uploadMediaFile,
} from "../../../services/api";
import { uploadSignature } from "../../../utils/agreementHelpers";
import { getPaymentType } from "../../../utils/deals/dealFormValueUtils";
import {
  normalizeTemplateFormData,
  validateStepData,
  validateWholeForm,
} from "../../../utils/deals/dealHelpers";
import { applyOutcomeRegistryDefaults } from "../../../utils/opportunities/outcomeRegistryHelpers";
import { getErrorMessage } from "../../../utils/utilMethods";
import {
  buildStepsFromFormData,
  getStepId,
  getTemplateSteps,
  getVisibleTemplateSteps,
  normalizeDealType,
  normalizeFighterSearchFields,
  removeUpfrontPaymentTiming,
} from "../../../utils/deals/dealModalHelpers";
import {
  prefetchCountries,
  prefetchFighters,
} from "./dealFormFields";
import { DealFormData } from "./dealFormTypes";
import OpportunityCardsList from "./opportunityCardList";
import OpportunityTypeToggle, {
  OpportunityTab,
} from "./opportunityTypeToggle";
import DealForm from "./dealForm";
import {
  AttachmentDto,
  DealAgreementResponseDto,
  DealComponentPaymentRequestDto,
  DealComponentPaymentResponseDto,
  DealModalProps,
  DealsProposalsRequestDto,
  MarketplaceCardResponseDto,
  OpportunityCardResponseDto,
  PaymentType,
  ProfileId,
  UpdateDealsProposalsRequestDto,
} from "../../../types/types";

const getDealTypeFromForm = (formData: DealFormData) =>
  (formData.core as { dealType?: string } | undefined)?.dealType;

const EmptyState = ({
  Icon,
  title,
  message,
}: {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: React.ReactNode;
  message: React.ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="p-4 bg-[#F4F4F5] rounded-full mb-4">
      <Icon className="w-10 h-10 text-[#A1A1AA]" />
    </div>
    <h3 className="text-base font-bold text-[#11181C] mb-1">{title}</h3>
    <p className="text-sm text-[#71717A]">{message}</p>
  </div>
);

const DealModal: React.FC<DealModalProps> = ({
  onClose,
  opportunityCards,
  onEdit,
  fighterId,
  isGroupChat = false,
  groupMembers = [],
  chatRoomId = null,
  editMode = false,
  dealToEdit = null,
}) => {
  const { t, i18n } = useTranslation("dealModal");

  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<OpportunityTab>("all");

  const [activeCard, setActiveCard] = useState<MarketplaceCardResponseDto | null>(null);
  const [draftCard, setDraftCard] = useState<MarketplaceCardResponseDto | null>(null);
  const [chosenFighterId, setChosenFighterId] = useState<ProfileId | null>(
    fighterId ?? null,
  );
  const [isOpeningCard, setIsOpeningCard] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  // Latest card request, so a slow response for an older click is ignored.
  const cardRequestId = useRef(0);
  // Uploads that already succeeded, so a retry after a failed create does not upload again.
  const uploadedFilesRef = useRef(new Map<File, AttachmentDto>());
  const uploadedSignatureRef = useRef<{ dataUrl: string; fileUrl: string } | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<AttachmentDto[]>([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCount, setDragCount] = useState(0);

  const [agreementPdfUrl, setAgreementPdfUrl] = useState<string | null>(null);
  const [savedSplitPayments, setSavedSplitPayments] = useState<
    DealComponentPaymentResponseDto[]
  >([]);

  const [editAgreement, setEditAgreement] = useState<
    DealAgreementResponseDto | null
  >(null);
  const [isAgreementLoading, setIsAgreementLoading] = useState(editMode);

  const {
    cards,
    page: cardPage,
    setPage: setCardPage,
    totalPages: cardTotalPages,
    isLoading: isLoadingCards,
    hasError: hasCardsError,
  } = useUpcomingOpportunityCards({
    tab: activeTab,
    fighterId,
    enabled: !editMode,
  });

  const targetFighterId = isGroupChat ? chosenFighterId : (fighterId ?? dealToEdit?.acceptedBy?.id ?? dealToEdit?.initiatedBy?.id ?? null);
  const needsSignature = true;
  const templateSteps = getVisibleTemplateSteps(draftCard);
  // Visible template steps + payments step + review step when a signature is needed.
  const totalSteps =
    templateSteps.length + 1 + (needsSignature ? 1 : 0);

  const availableFighters = groupMembers.filter(
    (member) => member.profileType === "FIGHTER",
  );

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem("i18nextLng") || "en");
  }, [i18n]);

  useEffect(() => {
    prefetchCountries();
    prefetchFighters();
  }, []);

  useEffect(() => {
    setChosenFighterId(fighterId ?? null);
  }, [fighterId]);

  // Free the preview PDF from memory when it is replaced or the modal closes.
  useEffect(() => {
    return () => {
      if (agreementPdfUrl) URL.revokeObjectURL(agreementPdfUrl);
    };
  }, [agreementPdfUrl]);

  useEffect(() => {
    if (!hasCardsError) return;
    toast.error(
      t(
        activeTab === "event"
          ? "errorLoadingEventOpportunities"
          : "errorLoadingOpportunities",
      ),
    );
  }, [hasCardsError, activeTab, t]);

  useEffect(() => {
    if (!editMode || !dealToEdit) {
      setDraftCard(null);
      setExistingAttachments([]);
      setSavedSplitPayments([]);
      return;
    }

    try {
      const matchingCard = opportunityCards.find(
        (card) => String(card.id) === String(dealToEdit.dealId ?? dealToEdit.id),
      );
      const cardWithTemplate =
        matchingCard ??
        opportunityCards.find((card) => getTemplateSteps(card).length > 0) ??
        opportunityCards[0];

      let templateJson =
        dealToEdit.templateJson ||
        cardWithTemplate?.opportunityCardResponse?.templateJson || { steps: [] };
      const savedFormData = dealToEdit.formData
        ? JSON.parse(JSON.stringify(dealToEdit.formData))
        : {};

      if (!templateJson.steps || templateJson.steps.length === 0) {
        const inferredSteps = buildStepsFromFormData(savedFormData, t);
        if (inferredSteps.length > 0) {
          templateJson = { steps: inferredSteps };
        }
      }

      const cardForEdit: MarketplaceCardResponseDto = {
        id: String(dealToEdit.id || 0),
        opportunityCardResponse: {
          id: String(dealToEdit.id || 0),
          templateJson,
          reported: false,
          contacted: false,
          status: dealToEdit.status,
          formData: applyOutcomeRegistryDefaults(
            normalizeTemplateFormData(
              templateJson.steps ?? [],
              savedFormData,
            ),
            templateJson.steps ?? [],
          ),
        },
      };

      setDraftCard(cardForEdit);
      setExistingAttachments(dealToEdit.fileMetadataList || []);
      setSavedSplitPayments(dealToEdit.componentPayments ?? []);

      if (dealToEdit.acceptedBy?.id) {
        setChosenFighterId(dealToEdit.acceptedBy.id);
      } else if (dealToEdit.initiatedBy?.id) {
        setChosenFighterId(dealToEdit.initiatedBy.id);
      }

      setActiveStep(0);
      setFormErrors({});
    } catch (error) {
      toast.error(t("errorPreparingEdit"));
    }
  }, [dealToEdit, editMode, isGroupChat, opportunityCards, t]);

  const dealId = dealToEdit?.id;

  // Loads the existing agreement for edit mode so the signature step can show
  // when a deal has an agreement. Returns null for legacy deals without one.
  useEffect(() => {
    if (!editMode || !dealId) {
      setIsAgreementLoading(false);
      setEditAgreement(null);
      return;
    }

    let isActive = true;
    setIsAgreementLoading(true);

    const loadAgreement = async () => {
      try {
        const agreement = await getDealAgreement(String(dealId));
        if (isActive) setEditAgreement(agreement);
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, t("errorOccurred")));
      } finally {
        if (isActive) setIsAgreementLoading(false);
      }
    };

    loadAgreement();

    return () => {
      isActive = false;
    };
  }, [dealId, editMode, t]);

  // Loads the selected opportunity card and opens the deal wizard for it.
  const startDealFromCard = async (card: MarketplaceCardResponseDto) => {
    const requestId = ++cardRequestId.current;
    setIsOpeningCard(true);
    setDraftCard(null);
    setActiveStep(0);
    setFormErrors({});
    setSavedSplitPayments([]);

    try {
      const response = await getOpportunityCard(card.id);
      if (requestId !== cardRequestId.current) return;
      const fullCard: MarketplaceCardResponseDto = response?.data ?? response;
      const fullOpportunity = fullCard.opportunityCardResponse;
      const cardSteps = getTemplateSteps(fullCard);

      setDraftCard({
        id: fullCard.id || card.id || "0",
        opportunityCardResponse: {
          ...fullOpportunity,
          id: fullOpportunity?.id || "0",
          templateJson: fullOpportunity?.templateJson || { steps: [] },
          formData: applyOutcomeRegistryDefaults(
            normalizeTemplateFormData(
              cardSteps,
              fullOpportunity?.formData || {},
            ),
            cardSteps,
          ),
        } as OpportunityCardResponseDto,
      });
    } catch (error: unknown) {
      if (requestId !== cardRequestId.current) return;
      toast.error(getErrorMessage(error, t("errorOccurred")));
    } finally {
      if (requestId === cardRequestId.current) setIsOpeningCard(false);
    }
  };

  // Closes the deal modal and notifies the parent.
  const closeModal = () => {
    setIsOpen(false);
    onClose();
  };

  // Uploads all selected files and returns their attachment metadata.
  const uploadSelectedFiles = async (): Promise<AttachmentDto[]> => {
    const uploadedFiles: AttachmentDto[] = [];

    for (const file of selectedFiles) {
      let attachment = uploadedFilesRef.current.get(file);
      if (!attachment) {
        attachment = {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          fileUrl: await uploadMediaFile(file),
        };
        uploadedFilesRef.current.set(file, attachment);
      }
      uploadedFiles.push(attachment);
    }

    return uploadedFiles;
  };

  // Uploads the signature once per drawn signature and reuses the result on retry.
  const uploadSignatureOnce = async (signatureDataUrl: string): Promise<string> => {
    const cached = uploadedSignatureRef.current;
    if (cached?.dataUrl === signatureDataUrl) return cached.fileUrl;

    const fileUrl = await uploadSignature(signatureDataUrl);
    uploadedSignatureRef.current = { dataUrl: signatureDataUrl, fileUrl };
    return fileUrl;
  };

  // Builds the final form data payload, stripping upfront payment timing if needed.
  const getFinalFormData = (): DealFormData => {
    const formData = applyOutcomeRegistryDefaults(
      draftCard?.opportunityCardResponse?.formData || {},
      templateSteps,
    );
    const preparedFormData = normalizeFighterSearchFields(formData, templateSteps);

    const isUpfrontPayment = getPaymentType(preparedFormData) === PaymentType.UPFRONT;

    return isUpfrontPayment
      ? removeUpfrontPaymentTiming(preparedFormData)
      : preparedFormData;
  };

  // Submits the deal (create or update) after validation and file upload.
  const submitDeal = async (
    componentPayments?: DealComponentPaymentRequestDto[],
    signatureDataUrl?: string,
  ) => {
    if (!draftCard) return;

    const { isValid, newErrors } = validateWholeForm(
      draftCard,
      getVisibleTemplateSteps,
      t,
      chosenFighterId,
      isGroupChat,
    );
    setFormErrors(newErrors);

    if (!isValid) {
      const firstErrorKey = Object.keys(newErrors)[0];
      const firstErrorStep = templateSteps.findIndex((step) => {
        const stepId = getStepId(step);
        return (
          firstErrorKey?.startsWith(`${stepId}.`) ||
          firstErrorKey?.startsWith(`${stepId}[`)
        );
      });

      if (firstErrorKey === "selectedFighterId") {
        setActiveStep(0);
      } else if (firstErrorStep >= 0) {
        setActiveStep(firstErrorStep);
      }
      return;
    }

    if (!targetFighterId) {
      toast.error(t("selectFighter"));
      return;
    }

    // Check the signature first, so we never upload files and then stop.
    if (needsSignature && !signatureDataUrl) {
      toast.error(t("pleaseSignAgreement"));
      return;
    }

    const paymentsToSend = componentPayments ?? [];

    try {
      setIsSubmitting(true);
      setIsUploadingFiles(true);

      const newAttachments = await uploadSelectedFiles();
      const keptAttachments = existingAttachments.filter(
        (attachment) =>
          attachment.fileUrl && !attachmentsToRemove.includes(attachment.fileUrl),
      );
      const attachments = [...keptAttachments, ...newAttachments];
      const formData = getFinalFormData();

      if (editMode && dealToEdit) {
        const updateData: UpdateDealsProposalsRequestDto = {
          dealId: dealToEdit.id,
          dealType: normalizeDealType(
            getDealTypeFromForm(formData) || dealToEdit.dealType,
          ),
          formData,
          componentPayments: paymentsToSend,
          fileMetadataList: attachments.length > 0 ? attachments : undefined,
        };

        if (signatureDataUrl) {
          updateData.signatureImage = await uploadSignatureOnce(signatureDataUrl);
        }

        await updateDealDetails(updateData);
      } else {
        const marketplaceCardId = String(draftCard.id ?? "").trim();
        if (!marketplaceCardId || marketplaceCardId.toLowerCase() === "nan") {
          toast.error(t("errorOccurred"));
          return;
        }

        const dealData: DealsProposalsRequestDto = {
          contactId: targetFighterId,
          marketplaceCardId,
          dealType: normalizeDealType(
            getDealTypeFromForm(formData) ||
              draftCard.opportunityCardResponse?.templateJson?.dealType ||
            dealToEdit?.dealType,
          ),
          chatType: isGroupChat ? "GROUP" : "INDIVIDUAL",
          chatRoomId: isGroupChat ? chatRoomId : null,
          formData,
          fileMetadataList: attachments,
          componentPayments: paymentsToSend,
        };

        if (signatureDataUrl) {
          dealData.signatureImage = await uploadSignatureOnce(signatureDataUrl);
        }

        await createDealProposal(dealData);
      }

      onEdit(draftCard);
      setSelectedFiles([]);
      setExistingAttachments([]);
      setAttachmentsToRemove([]);
      setSavedSplitPayments([]);
      setFormErrors({});
      closeModal();
      toast.success(t(editMode ? "dealUpdated" : "dealSentSuccessfully"));
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t("errorOccurred")));
    } finally {
      setIsSubmitting(false);
      setIsUploadingFiles(false);
    }
  };

  // Previews the deal agreement PDF before sending.
  const previewAgreement = async (
    componentPayments?: DealComponentPaymentRequestDto[],
  ): Promise<boolean> => {
    if (!draftCard) return false;
    if (!targetFighterId) {
      toast.error(t("selectFighter"));
      return false;
    }

    const formData = getFinalFormData();

    try {
      setIsSubmitting(true);
      const pdfBlob =
        editMode && dealToEdit
          ? await previewDealAgreementForUpdate({
              dealId: dealToEdit.id,
              dealType: normalizeDealType(
                getDealTypeFromForm(formData) || dealToEdit.dealType,
              ),
              formData,
              componentPayments: componentPayments ?? [],
            })
          : await previewDealAgreement({
              contactId: targetFighterId,
              marketplaceCardId: String(draftCard.id ?? "").trim(),
              dealType: normalizeDealType(
                getDealTypeFromForm(formData) ||
                  draftCard.opportunityCardResponse?.templateJson?.dealType ||
                  dealToEdit?.dealType,
              ),
              chatType: isGroupChat ? "GROUP" : "INDIVIDUAL",
              chatRoomId: isGroupChat ? chatRoomId : null,
              formData,
              fileMetadataList: [],
              componentPayments: componentPayments ?? [],
            });
      setAgreementPdfUrl(URL.createObjectURL(pdfBlob));
      return true;
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t("errorOccurred")));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validates the current step and moves to the next step or submits the deal.
  const handleNext = () => {
    if (!draftCard) return;

    const { isValid, errors } = validateStepData(
      activeStep,
      draftCard,
      getVisibleTemplateSteps,
      t,
      chosenFighterId,
      isGroupChat,
    );

    // The fighter is picked on the first step of the wizard.
    const isFighterMissing =
      activeStep === 0 && isGroupChat && chosenFighterId == null;

    if (!isValid || isFighterMissing) {
      const fighterError = isFighterMissing
        ? {
            selectedFighterId: t("requiredField", {
              field: t("selectFighter"),
            }),
          }
        : {};
      setFormErrors((previousErrors) => ({
        ...previousErrors,
        ...errors,
        ...fighterError,
      }));
      return;
    }

    if (activeStep < totalSteps - 1) {
      setActiveStep((step) => step + 1);
    } else {
      submitDeal();
    }
  };

  // Goes back: returns to the opportunity list if creating, otherwise closes the modal.
  const handleBack = () => {
    if (draftCard && !editMode) {
      setDraftCard(null);
    } else {
      closeModal();
    }
  };

  // Moves to the previous step in the deal flow.
  const handlePrevious = () => {
    if (activeStep > 0) setActiveStep((step) => step - 1);
    else handleBack();
  };

  const stepperProps = {
    isGroupChat,
    availableFighters,
    selectedFighterId: chosenFighterId,
    setSelectedFighterId: setChosenFighterId,
    errors: formErrors,
    setErrors: setFormErrors,
    editedCard: draftCard!,
    setEditedCard: setDraftCard,
    loading: isSubmitting || isUploadingFiles,
    selectedFiles,
    setSelectedFiles,
    existingAttachments,
    attachmentsToRemove,
    setAttachmentsToRemove,
    uploadingFiles: isUploadingFiles,
    isDragging,
    setIsDragging,
    dragCounter: dragCount,
    setDragCounter: setDragCount,
    editMode,
    onNext: handleNext,
    onPrevious: handlePrevious,
    onSubmit: submitDeal,
    onPreviewAgreement: previewAgreement,
    agreementPdfUrl,
    currentStep: activeStep,
    canProceed: !!draftCard,
    templateSteps,
    lockEventOpportunityFields: Boolean(
      draftCard?.opportunityCardResponse?.eventId,
    ),
    requiresSignature: needsSignature,
  };

  let modalTitle = t(editMode ? "updateDeal" : "makeADeal");
  if (draftCard && !editMode) {
    const opportunity = draftCard.opportunityCardResponse;
    modalTitle =
      opportunity?.templateJson?.templateName ||
      opportunity?.templateJson?.name ||
      opportunity?.eventName ||
      t("makeADeal");
  }

  const isWizardScreen = editMode || draftCard !== null;

  if (!isOpen) return null;

  const dragOverlay = isDragging && (
    <div className="absolute inset-0 bg-blue-500 bg-opacity-10 border-2 border-dashed border-blue-500 z-50 flex items-center justify-center rounded-xl">
      <div className="text-center">
        <DocumentIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
        <p className="text-lg font-semibold text-blue-700">
          {t("dropFilesHere")}
        </p>
        <p className="text-sm text-blue-600 mt-1">
          {t(VIDEO_UPLOAD_ENABLED ? "fileLimits" : "fileLimitsWithoutVideo")}
        </p>
      </div>
    </div>
  );

  const renderWizard = () => {
    if (editMode && isAgreementLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" color="primary" />
        </div>
      );
    }
    if (!draftCard) return null;
    return (
      <>
        <div className="flex items-center justify-between px-4 sm:px-8 pt-4 pb-3 shrink-0">
          <h3 className="text-base sm:text-xl font-bold text-[#11181C] truncate">
            {modalTitle}
          </h3>
          {isGroupChat && (
            <Chip
              color="secondary"
              variant="flat"
              startContent={<UserIcon className="w-4 h-4" />}
              className="font-semibold text-xs ml-2 shrink-0"
            >
              {t("groupChat")}
            </Chip>
          )}
        </div>
        <DealForm
          {...stepperProps}
          savedComponentPayments={editMode ? savedSplitPayments : []}
        />
      </>
    );
  };

  const renderOpportunityList = () => {
    if (isLoadingCards) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-gray-300 rounded-full border-t-blue-500" />
        </div>
      );
    }
    if (cards.length === 0) {
      return activeTab === "all" ? (
        <EmptyState
          Icon={TrophyIcon}
          title={t("noOpportunitiesAvailable")}
          message={t("createOpportunityCards")}
        />
      ) : (
        <EmptyState
          Icon={CalendarDaysIcon}
          title={t("noEventOpportunities")}
          message={t("noEventOpportunitiesMessage")}
        />
      );
    }
    return (
      <OpportunityCardsList
        validCards={cards}
        selectedCard={activeCard}
        handleCardClick={setActiveCard}
        t={t}
        cardPage={cardPage}
        onPageChange={setCardPage}
        cardTotalPages={cardTotalPages}
      />
    );
  };

  // Portal to document.body so the fixed overlay is not trapped by a transformed parent (the chat panel).
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:w-[1175px] sm:max-w-[95vw] h-full sm:h-auto sm:max-h-[94dvh] bg-white sm:rounded-3xl shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden font-['Plus_Jakarta_Sans']">
        <div className="flex items-center gap-3 px-4 sm:px-8 py-4 sm:py-5 border-b border-gray-100 shrink-0">
          {draftCard && !editMode && (
            <button
              type="button"
              onClick={handleBack}
              className="sm:hidden size-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors shrink-0"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-[#11181C] leading-6 truncate sm:pr-8">
              {t(isWizardScreen ? "dealWizard" : "makeADeal")}
            </h2>
            <p className="text-xs font-normal leading-5 text-[#71717A] max-w-4xl mt-0.5">
              {t(isWizardScreen ? "dealWizardDescription" : "makeADealDescription")}
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="hidden sm:flex size-6 items-center justify-center rounded-xl bg-[#F4F4F5] p-1 text-[#71717A] outline-none transition-colors hover:bg-[#E4E4E7] shrink-0"
            aria-label={t("cancel")}
          >
            <XMarkIcon className="size-4" strokeWidth={2} />
          </button>
        </div>

        <div
          className={`flex-1 min-h-0 relative ${
            isWizardScreen
              ? "flex flex-col"
              : "overflow-y-auto px-4 sm:px-8 py-4 sm:py-6 sm:min-h-[320px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          }`}
        >
          {dragOverlay}
          {isWizardScreen ? (
            renderWizard()
          ) : (
            <>
              <OpportunityTypeToggle
                activeTab={activeTab}
                onChange={setActiveTab}
                labels={{
                  all: t("allOpportunities"),
                  event: t("eventOpportunities"),
                }}
              />
              {renderOpportunityList()}
            </>
          )}
        </div>

        {!isWizardScreen && (
          <div className="flex justify-end px-4 sm:px-8 py-4 border-t border-gray-100 bg-white shrink-0">
            <Button
              color="primary"
              radius="full"
              className="w-full sm:w-auto h-11 sm:h-10 px-8 text-sm font-bold"
              isDisabled={!activeCard}
              isLoading={isOpeningCard}
              onPress={() => activeCard && startDealFromCard(activeCard)}
              endContent={<ArrowRightIcon className="w-4 h-4" />}
            >
              {t("next")}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default DealModal;
